import { CSSProperties, useEffect, useMemo, useRef, useState } from "react"
import { app, translate } from "../app"
import { languages } from "../locale"
import { Button } from "./button"
import { CloseOutlined } from "./icons"

const STORAGE_KEY = "signpost-popup-dismissed"

function buildPopupStyle(block: BlockPopup): CSSProperties {
  const styles: CSSProperties = {}
  if (block.textsize) styles.fontSize = `${block.textsize}%`
  if (block.textcolor) styles.color = block.textcolor
  if (block.bgcolor) styles.backgroundColor = block.bgcolor
  if (block.fontweight) styles.fontWeight = block.fontweight
  return styles
}

function popupFingerprint(block: BlockPopup): string {
  const title = translate(block.popup_title ?? block.title)
  const subtitle = translate(block.subtitle)
  const text = translate(block.popup_text ?? block.text)
  return [block.sort ?? "", title, subtitle, text].join("|")
}

function wasDismissed(fingerprint: string): boolean {
  try {
    return localStorage.getItem(STORAGE_KEY) === fingerprint
  } catch {
    return false
  }
}

function markDismissed(fingerprint: string) {
  try {
    localStorage.setItem(STORAGE_KEY, fingerprint)
  } catch {
  }
}

export function BlockPopup(props: { block: BlockPopup }) {
  const { block } = props
  const isRTL = languages[app.locale]?.rtl
  const dialogRef = useRef<HTMLDivElement>(null)
  const fingerprint = useMemo(() => popupFingerprint(block), [block, app.locale])

  const title = translate(block.popup_title ?? block.title)
  const subtitle = translate(block.subtitle)
  const text = translate(block.popup_text ?? block.text)
  const buttonText = translate(block.button_text)
  const hasContent = Boolean(title || subtitle || text)
  const dismissed = wasDismissed(fingerprint)
  const shouldOpen = hasContent && !dismissed

  const [open, setOpen] = useState(() => shouldOpen)

  useEffect(() => {
    setOpen(shouldOpen)
  }, [fingerprint, hasContent, shouldOpen])

  useEffect(() => {
    if (!open) return

    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = "hidden"
    dialogRef.current?.focus()

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        markDismissed(fingerprint)
        setOpen(false)
      }
    }

    window.addEventListener("keydown", onKeyDown)
    return () => {
      document.body.style.overflow = previousOverflow
      window.removeEventListener("keydown", onKeyDown)
    }
  }, [open, fingerprint])

  const dismiss = () => {
    markDismissed(fingerprint)
    setOpen(false)
  }

  if (!open || !hasContent) {
    return null
  }

  const direction = isRTL ? "rtl" : "ltr"
  const align = isRTL ? "right" : "left"
  const styles = buildPopupStyle(block)

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-6"
      role="presentation"
    >
      <div
        className="absolute inset-0 bg-black/55"
        onClick={dismiss}
        aria-hidden="true"
      />
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? "urgent-popup-title" : undefined}
        tabIndex={-1}
        className="relative z-[101] w-full max-w-lg max-h-[85vh] overflow-y-auto rounded-xl border border-solid border-[#d7dde5] bg-white shadow-xl outline-none"
        style={styles}
      >
        <div className="sticky top-0 flex justify-end bg-white/95 px-3 pt-3">
          <button
            type="button"
            onClick={dismiss}
            className="inline-flex h-9 w-9 items-center justify-center rounded-full border-none bg-transparent text-gray-600 hover:bg-gray-100 hover:text-gray-900 cursor-pointer"
            aria-label="Close"
          >
            <CloseOutlined className="text-xl" aria-hidden="true" />
          </button>
        </div>

        <div
          className="px-5 pb-6 pt-1 sm:px-7 sm:pb-7"
          style={{ direction, textAlign: align }}
        >
          {title && (
            <h2
              id="urgent-popup-title"
              className="m-0 text-2xl font-bold leading-snug text-[#111827]"
            >
              {title}
            </h2>
          )}
          {subtitle && (
            <p className="mt-2 mb-0 text-base font-medium leading-6 text-[#374151]">
              {subtitle}
            </p>
          )}
          {text && (
            <div
              className="mt-4 text-sm leading-6 text-[#1f2937] [&_a]:text-blue-600 [&_a]:underline [&_p]:my-2 [&_ul]:my-2 [&_ol]:my-2"
              dangerouslySetInnerHTML={{ __html: text }}
            />
          )}

          <div className={`mt-6 flex ${isRTL ? "justify-start" : "justify-end"}`}>
            {block.button_link && buttonText ? (
              <a
                href={block.button_link}
                target="_blank"
                rel="noopener noreferrer"
                className="no-underline"
                onClick={dismiss}
              >
                <Button className="!h-10 !px-5 !leading-8">{buttonText}</Button>
              </a>
            ) : (
              <Button className="!h-10 !px-5 !leading-8" onClick={dismiss}>
                {buttonText || "OK"}
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
